/**
 * /cli-login — approve a waiting `tile login`.
 *
 * The CLI starts a sign-in on tile-auth and prints a short code plus this
 * page's URL, then polls tile-auth until someone approves. This page is that
 * someone: sign in (any way the web app allows — Google included), check the
 * code matches the terminal, and Authorize. The CLI picks the token up on its
 * next poll.
 *
 * The CLI and this page never talk to each other, which is why this works
 * from a phone or a laptop while the CLI runs over SSH or in a container.
 * Opened without `?code=`, the page asks for the code, so on a phone you can
 * type the short URL and the eight characters instead of a long link.
 *
 * Security — approving hands a session to whoever started the request, so:
 *   • nothing happens without an explicit click;
 *   • the code, when it and the request's origin are shown next to each other,
 *     is what lets you spot a request you didn't start (someone sending you
 *     their code to approve) before you authorize it;
 *   • signed out, you go through /login and come back here, so the approval
 *     only ever carries a session you just proved you own.
 */
import React, { FormEvent, useEffect, useState } from 'react';
import { RouteComponentProps } from 'react-router-dom';

import AuthCard from '@/features/auth/components/auth-card/AuthCard';
import { getSession } from '@/features/auth/session/authSession';

interface CliRequest {
  userCode: string;
  status: 'pending' | 'approved' | 'denied';
  requestedAt: string;
  expiresAt: string;
  ip: string | null;
  userAgent: string | null;
}

type Phase =
  | { kind: 'enter' }
  | { kind: 'checking' }
  | { kind: 'ready'; req: CliRequest; email: string }
  | { kind: 'working'; req: CliRequest; email: string }
  | { kind: 'approved' }
  | { kind: 'denied' }
  | { kind: 'gone'; message: string };

const codeStyle: React.CSSProperties = {
  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
  fontSize: 26,
  letterSpacing: 3,
  textAlign: 'center',
  margin: '6px 0 14px',
};

async function post(path: string, userCode: string): Promise<Response> {
  return fetch(path, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'same-origin',
    body: JSON.stringify({ userCode }),
  });
}

async function errorMessage(res: Response, fallback: string): Promise<string> {
  const data = (await res.json().catch(() => ({}))) as { message?: string };
  return data.message || fallback;
}

export default function CliLogin(props: RouteComponentProps) {
  const code = new URLSearchParams(props.location.search).get('code') || '';
  const [phase, setPhase] = useState<Phase>(code ? { kind: 'checking' } : { kind: 'enter' });
  const [typed, setTyped] = useState('');

  useEffect(() => {
    if (!code) {
      setPhase({ kind: 'enter' });
      return undefined;
    }
    let cancelled = false;
    setPhase({ kind: 'checking' });
    (async () => {
      const session = await getSession();
      if (cancelled) return;
      if (!session) {
        // Sign in (or sign up), then come straight back with the same code.
        const back = encodeURIComponent(`${props.location.pathname}${props.location.search}`);
        window.location.replace(`/login?redirect=${back}`);
        return;
      }
      try {
        const res = await fetch(`/api/auth/cli/request?code=${encodeURIComponent(code)}`, {
          credentials: 'same-origin',
        });
        if (cancelled) return;
        if (!res.ok) {
          setPhase({
            kind: 'gone',
            message: await errorMessage(res, 'This sign-in request has expired. Run `tile login` again.'),
          });
          return;
        }
        const req = (await res.json()) as CliRequest;
        if (req.status !== 'pending') {
          setPhase({ kind: 'gone', message: 'This request was already approved or denied.' });
          return;
        }
        setPhase({ kind: 'ready', req, email: session.user.email || '' });
      } catch {
        if (!cancelled) setPhase({ kind: 'gone', message: 'Could not reach the server. Try again.' });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [code, props.location.pathname, props.location.search]);

  const decide = async (verb: 'approve' | 'deny'): Promise<void> => {
    if (phase.kind !== 'ready') return;
    setPhase({ ...phase, kind: 'working' });
    try {
      const res = await post(`/api/auth/cli/${verb}`, phase.req.userCode);
      if (!res.ok) {
        setPhase({ kind: 'gone', message: await errorMessage(res, 'Could not complete that. Try again.') });
        return;
      }
      setPhase({ kind: verb === 'approve' ? 'approved' : 'denied' });
    } catch {
      setPhase({ kind: 'gone', message: 'Could not reach the server. Try again.' });
    }
  };

  const onEnter = (e: FormEvent) => {
    e.preventDefault();
    const clean = typed.trim();
    if (clean) props.history.replace(`/cli-login?code=${encodeURIComponent(clean)}`);
  };

  if (phase.kind === 'enter') {
    return (
      <AuthCard title="Connect the Tile CLI" subtitle="Enter the code shown in your terminal.">
        <form onSubmit={onEnter} noValidate>
          <input
            className="auth-input"
            style={{ ...codeStyle, fontSize: 20 }}
            placeholder="ABCD-EFGH"
            autoCapitalize="characters"
            autoComplete="off"
            spellCheck={false}
            value={typed}
            onChange={(e) => setTyped(e.target.value)}
          />
          <button type="submit" className="auth-btn auth-btn-primary" disabled={!typed.trim()}>
            Continue
          </button>
        </form>
      </AuthCard>
    );
  }

  if (phase.kind === 'checking') {
    return <AuthCard title="Connect the Tile CLI" subtitle="Checking the request…" />;
  }

  if (phase.kind === 'approved') {
    return (
      <AuthCard
        title="CLI connected"
        subtitle="Your terminal will pick this up in a few seconds. You can close this tab."
      />
    );
  }

  if (phase.kind === 'denied') {
    return <AuthCard title="Request denied" subtitle="The CLI was not signed in. You can close this tab." />;
  }

  if (phase.kind === 'gone') {
    return (
      <AuthCard title="Can't connect the CLI" subtitle={phase.message}>
        <p className="auth-foot">Return to your terminal and run `tile login` again.</p>
      </AuthCard>
    );
  }

  const { req, email } = phase;
  const busy = phase.kind === 'working';
  const when = new Date(req.requestedAt).toLocaleTimeString();

  return (
    <AuthCard
      title="Connect the Tile CLI"
      subtitle={email ? `This will sign the CLI in as ${email}.` : 'This will sign the CLI in as you.'}
    >
      <p className="auth-foot" style={{ marginTop: 0 }}>
        Your terminal should show this code:
      </p>
      <div style={codeStyle}>{req.userCode}</div>
      <p className="auth-foot" style={{ marginTop: 0 }}>
        Requested at {when}
        {req.ip ? ` from ${req.ip}` : ''}
        {req.userAgent ? ` · ${req.userAgent}` : ''}
      </p>
      <p className="auth-foot">
        If the code doesn&apos;t match, or you didn&apos;t just run <code>tile login</code>, deny it —
        someone may be trying to get you to sign in their CLI.
      </p>

      <button
        type="button"
        onClick={() => decide('approve')}
        disabled={busy}
        className="auth-btn auth-btn-primary"
      >
        {busy ? 'Working…' : 'Authorize'}
      </button>
      <button
        type="button"
        onClick={() => decide('deny')}
        disabled={busy}
        className="auth-btn"
        style={{ marginTop: 8 }}
      >
        Deny
      </button>
    </AuthCard>
  );
}
